import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tenantSettingsService } from '../services/tenantSettingsService'
import type { TenantSettings } from '../types/tenantSettings'
import './SettingsStc.css'

export function SettingsStc() {
  const queryClient = useQueryClient()
  const [stcGoogleDriveFolderId, setStcGoogleDriveFolderId] = useState('')
  const [stcGoogleClientId, setStcGoogleClientId] = useState('')
  const [stcWacomLicenceId, setStcWacomLicenceId] = useState('')
  const [stcWacomLicenceKey, setStcWacomLicenceKey] = useState('')
  const [showWacomKey, setShowWacomKey] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const { data: tenantSettings, isLoading } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => tenantSettingsService.getCurrent(),
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<TenantSettings>) => tenantSettingsService.update(data),
    onSuccess: (updated) => {
      queryClient.setQueryData(['tenant-settings'], updated)
      setSaveMessage({ type: 'success', text: 'STC settings saved successfully.' })
      setStcWacomLicenceKey('')
      setTimeout(() => setSaveMessage(null), 3000)
    },
    onError: (error: Error) => {
      setSaveMessage({ type: 'error', text: error.message || 'Failed to save settings' })
    },
  })

  useEffect(() => {
    if (tenantSettings) {
      setStcGoogleDriveFolderId(tenantSettings.stcGoogleDriveFolderId ?? '')
      setStcGoogleClientId(tenantSettings.stcGoogleClientId ?? '')
      setStcWacomLicenceId(tenantSettings.stcWacomLicenceId ?? '')
    }
  }, [tenantSettings])

  const handleSave = () => {
    setSaveMessage(null)
    const licenceId = stcWacomLicenceId.trim() || null
    const licenceKey = stcWacomLicenceKey.trim() || null
    if (typeof sessionStorage !== 'undefined') {
      if (licenceId && licenceKey) {
        sessionStorage.setItem('clnicl_stc_wacom_licence_id', licenceId)
        sessionStorage.setItem('clnicl_stc_wacom_licence_key', licenceKey)
      } else {
        sessionStorage.removeItem('clnicl_stc_wacom_licence_id')
        sessionStorage.removeItem('clnicl_stc_wacom_licence_key')
      }
    }
    updateMutation.mutate({
      sessionTimeoutMinutes: tenantSettings?.sessionTimeoutMinutes ?? 30,
      stcGoogleDriveFolderId: stcGoogleDriveFolderId.trim() || null,
      stcGoogleClientId: stcGoogleClientId.trim() || null,
      stcWacomLicenceId: licenceId,
      ...(licenceKey ? { stcWacomLicenceKey: licenceKey } : {}),
    })
  }

  if (isLoading) {
    return (
      <div className="settings-stc">
        <p>Loading settings…</p>
      </div>
    )
  }

  return (
    <div className="settings-stc">
      <div className="settings-stc-header">
        <h2>STC &amp; PCRS</h2>
        <p className="settings-stc-subtitle">
          Configure Special Type Consultation (STC) and Mother &amp; Infant Care Scheme (MICS) signing. Optional: Google Drive folder and Wacom signature pad licence.
        </p>
      </div>

      <div className="settings-stc-content">
        <section className="settings-stc-section" aria-labelledby="stc-google-heading">
          <h3 id="stc-google-heading">Google Drive (optional)</h3>
          <p className="form-hint">
            When set, the STC signing form can save the generated PNG to a Google Drive folder. You need a Google Cloud OAuth 2.0 Web client ID and the target folder ID.
          </p>
          <div className="form-group">
            <label htmlFor="stc-google-folder-id">Drive folder ID</label>
            <input
              id="stc-google-folder-id"
              type="text"
              value={stcGoogleDriveFolderId}
              onChange={(e) => setStcGoogleDriveFolderId(e.target.value)}
              placeholder="e.g. 1Cyu7uGmgRlS_g-mL7kXH1lG_K0T9-29i"
              className="form-control"
              aria-describedby="stc-google-folder-hint"
            />
            <p id="stc-google-folder-hint" className="form-hint">
              The ID of the Google Drive folder where STC/MICS forms will be uploaded. Find it in the folder URL in Drive.
            </p>
          </div>
          <div className="form-group">
            <label htmlFor="stc-google-client-id">Google OAuth 2.0 Client ID (Web)</label>
            <input
              id="stc-google-client-id"
              type="text"
              value={stcGoogleClientId}
              onChange={(e) => setStcGoogleClientId(e.target.value)}
              placeholder="xxxxx.apps.googleusercontent.com"
              className="form-control"
              aria-describedby="stc-google-client-hint"
            />
            <p id="stc-google-client-hint" className="form-hint">
              Create a Web application OAuth 2.0 client in Google Cloud Console. Add your app origin to authorised JavaScript origins. Scope used: drive.file.
            </p>
          </div>
        </section>

        <section className="settings-stc-section" aria-labelledby="stc-wacom-heading">
          <h3 id="stc-wacom-heading">Wacom signature pad (optional)</h3>
          <p className="form-hint">
            Licence for Wacom SigCaptX SDK used for signature capture on a Wacom STU device. Leave blank if not using a signature pad.
          </p>
          <div className="form-group">
            <label htmlFor="stc-wacom-licence-id">Licence ID</label>
            <input
              id="stc-wacom-licence-id"
              type="text"
              value={stcWacomLicenceId}
              onChange={(e) => setStcWacomLicenceId(e.target.value)}
              placeholder="UUID from Wacom"
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label htmlFor="stc-wacom-licence-key">Licence key</label>
            <div className="api-key-input-wrapper">
              <input
                id="stc-wacom-licence-key"
                type={showWacomKey ? 'text' : 'password'}
                value={stcWacomLicenceKey}
                onChange={(e) => setStcWacomLicenceKey(e.target.value)}
                placeholder={tenantSettings?.hasStcWacomLicence ? '••••••••' : 'Enter key'}
                className="form-control"
                aria-describedby="stc-wacom-key-hint"
              />
              <button
                type="button"
                className="toggle-api-key-btn"
                onClick={() => setShowWacomKey(!showWacomKey)}
                title={showWacomKey ? 'Hide key' : 'Show key'}
                aria-label={showWacomKey ? 'Hide licence key' : 'Show licence key'}
              >
                {showWacomKey ? '🙈' : '👁️'}
              </button>
            </div>
            <p id="stc-wacom-key-hint" className="form-hint">
              {tenantSettings?.hasStcWacomLicence
                ? 'Leave blank to keep the existing key. Enter a new key to replace it.'
                : 'Enter the licence key from Wacom. It is stored securely and not shown again.'}
            </p>
          </div>
        </section>

        {saveMessage && (
          <div className={`save-message ${saveMessage.type}`} role="alert">
            {saveMessage.text}
          </div>
        )}

        <div className="settings-stc-actions">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="btn-primary"
          >
            {updateMutation.isPending ? 'Saving…' : 'Save STC settings'}
          </button>
        </div>
      </div>
    </div>
  )
}
